import { createFileRoute, Link } from '@tanstack/react-router'
import { format } from 'date-fns'
import { id } from 'date-fns/locale'
import {
  AlertCircle,
  DollarSign,
  Package,
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react'
import { useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'

import { PageLayout } from '@/components/layout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { CashFlowChart } from '@/features/cashflow/components/cash-flow-chart'

import { toIdr } from '@/libs/utils/format'

export const Route = createFileRoute('/_app/report/')({
  component: Component,
})

function Component() {
  return (
    <PageLayout>
      <DashboardPage />
      {/* <PageCard.Root>
        <PageCard.Header
          title="Arus Kas"
          desc="Menampilkan pergerakan uang masuk dan keluar dari transaksi dalam periode tertentu."
          actions={
            <PageCard.HeaderActionRoot className="shrink-0">
              <PageCard.HeaderAction asChild>
                <Link from={Route.fullPath} to="/report/cash-flow">
                  Lihat laporan
                </Link>
              </PageCard.HeaderAction>
            </PageCard.HeaderActionRoot>
          }
        />
        <PageCard.Content>
          <PageCard.ContentSection>
            <div className="col-span-full">
              <CashFlowChart />
            </div>
          </PageCard.ContentSection>
        </PageCard.Content>
      </PageCard.Root> */}
    </PageLayout>
  )
}

interface Outlet {
  id: string
  name: string
}

const OUTLETS: Outlet[] = [
  { id: 'ikki_coffee', name: 'Ikki Coffee' },
  { id: 'ikki_resto', name: 'Ikki Resto' },
]

const mockSummaryData = {
  ikki_coffee: {
    totalRevenue: 44000000,
    totalCOGS: 27700000,
    grossProfit: 16300000,
    netProfit: 4550000,
    totalTransactions: 842,
    avgTransaction: 52256,
    topProduct: 'Latte',
    topProductSales: 2850000,
    lowStock: [
      { name: 'Biji Kopi Arabica', stock: 3, unit: 'kg' },
      { name: 'Susu UHT', stock: 8, unit: 'liter' },
    ],
    revenueTrend: [
      { date: '2025-10-28', revenue: 3200000 },
      { date: '2025-10-29', revenue: 4100000 },
      { date: '2025-10-30', revenue: 3800000 },
      { date: '2025-10-31', revenue: 4500000 },
      { date: '2025-11-01', revenue: 5200000 },
      { date: '2025-11-02', revenue: 4800000 },
      { date: '2025-11-03', revenue: 5500000 },
    ],
    profitTrend: [
      { month: 'Jul', profit: 2800000 },
      { month: 'Agu', profit: 3200000 },
      { month: 'Sep', profit: 3800000 },
      { month: 'Okt', profit: 4200000 },
      { month: 'Nov', profit: 4550000 },
    ],
  },
  ikki_resto: {
    totalRevenue: 66400000,
    totalCOGS: 42700000,
    grossProfit: 23700000,
    netProfit: 7200000,
    totalTransactions: 1120,
    avgTransaction: 59286,
    topProduct: 'Nasi Goreng Spesial',
    topProductSales: 4200000,
    lowStock: [
      { name: 'Beras Premium', stock: 5, unit: 'kg' },
      { name: 'Ayam Potong', stock: 12, unit: 'kg' },
    ],
    revenueTrend: [
      { date: '2025-10-28', revenue: 4800000 },
      { date: '2025-10-29', revenue: 5500000 },
      { date: '2025-10-30', revenue: 6200000 },
      { date: '2025-10-31', revenue: 6800000 },
      { date: '2025-11-01', revenue: 7200000 },
      { date: '2025-11-02', revenue: 6900000 },
      { date: '2025-11-03', revenue: 7500000 },
    ],
    profitTrend: [
      { month: 'Jul', profit: 4200000 },
      { month: 'Agu', profit: 4800000 },
      { month: 'Sep', profit: 5500000 },
      { month: 'Okt', profit: 6800000 },
      { month: 'Nov', profit: 7200000 },
    ],
  },
} as const

function DashboardPage() {
  const [outletId, setOutletId] = useState('ikki_coffee' as const)
  const data = mockSummaryData[outletId]

  const revenueChartConfig = {
    revenue: {
      label: 'Pendapatan',
      color: 'hsl(var(--chart-1))',
    },
  } satisfies ChartConfig

  const profitChartConfig = {
    profit: {
      label: 'Laba Bersih',
      color: 'hsl(var(--chart-2))',
    },
  } satisfies ChartConfig

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div
        className={`
          flex flex-col items-start justify-between gap-4
          md:flex-row md:items-center
        `}
      >
        <div>
          <h1
            className={`
              text-3xl font-bold text-gray-900
              dark:text-white
            `}
          >
            Dashboard Summary
          </h1>
          <p
            className={`
              mt-1 text-sm text-gray-500
              dark:text-gray-400
            `}
          >
            Ringkasan kinerja keuangan dan operasional
          </p>
        </div>
        <Select value={outletId} onValueChange={setOutletId}>
          <SelectTrigger className='w-[180px]'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OUTLETS.map(o => (
              <SelectItem key={o.id} value={o.id}>
                {o.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div
        className={`
          grid grid-cols-1 gap-4
          md:grid-cols-2
          lg:grid-cols-4
        `}
      >
        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Pendapatan</CardTitle>
            <DollarSign className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{toIdr(data.totalRevenue)}</div>
            <p className='text-xs text-muted-foreground'>
              <TrendingUp className='inline h-3 w-3 text-green-600' /> +12.5% dari bulan lalu
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Laba Bersih</CardTitle>
            <TrendingUp className='h-4 w-4 text-green-600' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold text-green-600'>{toIdr(data.netProfit)}</div>
            <p className='text-xs text-muted-foreground'>
              Margin: {((data.netProfit / data.totalRevenue) * 100).toFixed(1)}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Transaksi</CardTitle>
            <ShoppingCart className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{data.totalTransactions}</div>
            <p className='text-xs text-muted-foreground'>
              Rata-rata: {toIdr(data.avgTransaction)}/transaksi
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Produk Terlaris</CardTitle>
            <Package className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-lg font-semibold'>{data.topProduct}</div>
            <p className='text-xs text-muted-foreground'>
              Penjualan: {toIdr(data.topProductSales)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue='revenue' className='space-y-4'>
        <TabsList>
          <TabsTrigger value='revenue'>Trend Pendapatan</TabsTrigger>
          <TabsTrigger value='profit'>Trend Laba</TabsTrigger>
          <TabsTrigger value='alerts'>Peringatan Stok</TabsTrigger>
        </TabsList>

        <TabsContent value='revenue' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>Pendapatan Harian (7 Hari Terakhir)</CardTitle>
              <CardDescription>{format(new Date(), 'd MMMM yyyy', { locale: id })}</CardDescription>
            </CardHeader>
            <CardContent className='pl-2'>
              <ChartContainer config={revenueChartConfig} className='h-80'>
                <ResponsiveContainer width='100%' height='100%'>
                  <LineChart
                    data={data.revenueTrend}
                    margin={{ top: 5, right: 10, left: 10, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis
                      dataKey='date'
                      tickFormatter={value => format(new Date(value), 'dd MMM', { locale: id })}
                    />
                    <YAxis tickFormatter={value => `Rp${(value / 1000000).toFixed(0)}jt`} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line
                      type='monotone'
                      dataKey='revenue'
                      stroke='var(--color-revenue)'
                      strokeWidth={2}
                      dot={{ fill: 'var(--color-revenue)' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='profit' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>Laba Bersih Bulanan</CardTitle>
              <CardDescription>5 bulan terakhir</CardDescription>
            </CardHeader>
            <CardContent className='pl-2'>
              <ChartContainer config={profitChartConfig} className='h-80'>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart data={data.profitTrend}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='month' />
                    <YAxis tickFormatter={value => `Rp${(value / 1000000).toFixed(0)}jt`} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey='profit' fill='var(--color-profit)' radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='alerts' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <AlertCircle className='h-5 w-5 text-orange-600' />
                Peringatan Stok Rendah
              </CardTitle>
              <CardDescription>Stok di bawah ambang batas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className='space-y-3'>
                {data.lowStock.map((item, i) => (
                  <div
                    key={i}
                    className={`
                      flex items-center justify-between rounded-lg bg-orange-50 p-3
                      dark:bg-orange-900/20
                    `}
                  >
                    <div>
                      <p className='font-medium'>{item.name}</p>
                      <p className='text-sm text-muted-foreground'>Stok tersisa</p>
                    </div>
                    <div className='text-right'>
                      <p className='text-2xl font-bold text-orange-600'>{item.stock}</p>
                      <p className='text-xs text-muted-foreground'>{item.unit}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
